import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import BrandStorySection from './components/BrandStorySection';
import ProductHighlights from './components/ProductHighlights';
import RollingBottleSection from './components/RollingBottleSection';
import SustainabilityCounter from './components/SustainabilityCounter';
import TestimonialsSection from './components/TestimonialsSection';
import NewsletterSection from './components/NewsletterSection';
import CTASection from './components/CTASection';
import WhatsAppButton from './components/WhatsAppButton';

const Homepage = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time for animations
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Preloader Component
  const Preloader = () =>
  <motion.div
    initial={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5 }}
    className="fixed inset-0 z-50 bg-mava-gradient flex items-center justify-center">

      <div className="text-center">
        <motion.div
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 p-2">

          <Image
            src="/assets/images/mava-1762174454410.jpg"
            alt="Mava Cold Beverages Logo Loading"
            className="w-full h-full object-contain rounded-lg"
          />
        </motion.div>
        
        <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="mb-2">

          <h1 className="text-4xl font-poppins font-bold text-white">
            ma
            <span className="relative inline-block">
              v
              <motion.span
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-1 -right-1 text-green-300 text-lg">

                🍃
              </motion.span>
            </span>
            a
          </h1>
        </motion.div>
        
        <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.8 }}
        className="text-white/90 font-inter text-lg">

          Cold Beverages
        </motion.p>

        {/* Loading Animation */}
        <motion.div
        initial={{ width: 0 }}
        animate={{ width: "100%" }}
        transition={{ delay: 1, duration: 1 }}
        className="w-40 h-1 bg-white/30 rounded-full mx-auto mt-8 overflow-hidden">

          <motion.div
          animate={{ x: ["-100%", "100%"] }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="h-full w-1/2 bg-white rounded-full" />

        </motion.div>

        <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="text-white/70 font-inter text-sm mt-4">

          Sip the freshness, live the moment
        </motion.p>
      </div>
    </motion.div>;


  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Mava Cold Beverages - Pure Water & Refreshing Drinks | Drink Fresh. Live Smart.</title>
        <meta
          name="description"
          content="Mava Cold Beverages offers pure bottled water and refreshing drinks including Jeera Drink, Cola, Mango Juice, and Goli Soda. Experience purity, freshness, and premium quality beverages." />

        <meta
          name="keywords"
          content="Mava, cold beverages, pure water, bottled water, jeera drink, cola, mango juice, goli soda, refreshing drinks, Indian beverages, premium water" />

        <meta name="author" content="Mava Cold Beverages" />
        <meta property="og:title" content="Mava Cold Beverages - Drink Fresh. Live Smart." />
        <meta
          property="og:description"
          content="Premium cold beverages starting with pure water and expanding to exciting flavors. Join the Mava journey of refreshment." />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mava.in" />
        <link rel="canonical" href="https://mava.in" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Mava Cold Beverages",
            "description": "Premium cold beverages and pure bottled water company",
            "url": "https://mava.in",
            "logo": "https://images.unsplash.com/photo-1626810257238-19db99ca6c4c",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-98765-43210",
              "contactType": "customer service"
            },
            "sameAs": [
            "https://facebook.com/mava",
            "https://instagram.com/mava",
            "https://twitter.com/mava"]

          })}
        </script>
      </Helmet>
      <AnimatePresence>
        {isLoading && <Preloader />}
      </AnimatePresence>
      <AnimatePresence>
        {!isLoading &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen bg-background">

            {/* Header */}
            <Header />

            {/* Hero Section */}
            <HeroSection />

            {/* Brand Story Section */}
            <BrandStorySection />

            {/* Product Highlights */}
            <ProductHighlights />

            {/* Rolling Bottle Section */}
            <RollingBottleSection />

            {/* Sustainability Counter */}
            <SustainabilityCounter />

            {/* Testimonials Section */}
            <TestimonialsSection />

            {/* Newsletter Section */}
            <NewsletterSection />

            {/* CTA Section */}
            <CTASection />

            {/* WhatsApp Floating Button */}
            <WhatsAppButton />

            {/* Scroll to Top Button */}
            <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 3, duration: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-8 right-8 z-40 w-14 h-14 bg-mava-gradient rounded-full flex items-center justify-center liquid-shadow hover:scale-110 brand-transition cursor-glow">

              <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity }}>

                <Icon name="ArrowUp" size={24} color="white" />
              </motion.div>
            </motion.button>
          </motion.div>
        }
      </AnimatePresence>
    </>
  );

};

export default Homepage;